import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Loader2, ShieldAlert } from "lucide-react";
import { api } from "@/lib/api";
import { usePermissions } from "@/lib/permissions";
import { getErrorMessage } from "@/lib/errors";
import { fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

/** What stands between this project and ACTIVE (signed contract or an approved exception, scope, approved baseline budget).
 *  Nothing is shown for a project that is fully set up, or one that was already running when the gates came in. */
export function ControlGatesCard({ projectId, onChanged }) {
  const { can } = usePermissions();
  const [state, setState] = useState(null);
  const [asking, setAsking] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { setState((await api.get(`/projects/${projectId}/control-gates`)).data); } catch { setState(null); }
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  if (!state || !state.enforced || state.exempt) return null;
  const waiting = state.blockers.length > 0;
  if (!waiting && !state.exception) return null;
  const missingContract = state.blockers.some((b) => b.code === "NO_CONTRACT");
  const canApprove = can("PROJECT_EXCEPTIONS", "approve");

  const approve = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/control-exception`, { reason: reason.trim() });
      setState(data); setAsking(false); setReason("");
      toast.success("Exception approved. The project can start without the signed contract.");
      onChanged?.();
    } catch (err) { toast.error(getErrorMessage(err)); } finally { setBusy(false); }
  };

  return (
    <div className={`rounded-xl border p-4 ${waiting ? "border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-950/30" : "border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-900"}`} data-testid="control-gates-card">
      {waiting ? (
        <>
          <div className="flex items-center gap-2 text-sm font-semibold text-amber-900 dark:text-amber-200"><ShieldAlert className="h-4 w-4" /> This project is in Planning until it has what the control gates require</div>
          <ul className="mt-2 space-y-1 text-sm text-amber-900 dark:text-amber-100" data-testid="control-gates-blockers">
            {state.blockers.map((b) => <li key={b.code} className="flex gap-2"><span aria-hidden>•</span><span>{b.message}</span></li>)}
          </ul>
          <p className="mt-2 text-xs text-amber-800/80 dark:text-amber-200/70">It becomes Active by itself as soon as the last item is done. Orders cannot be raised against it before then.</p>
          {missingContract && canApprove && <Button size="sm" variant="outline" className="mt-3" onClick={() => setAsking(true)} data-testid="control-exception-open">Approve an exception (start without a signed contract)</Button>}
          {missingContract && !canApprove && <p className="mt-2 text-xs text-amber-800/80">Starting without a signed contract needs an exception approved by an Admin or Portfolio Lead.</p>}
        </>
      ) : (
        <div className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-200" data-testid="control-gates-exception">
          <CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-600" />
          <span>Started on an approved exception{state.exception?.at ? ` (${fmtDate(state.exception.at)})` : ""}: {state.exception?.reason}. Link the signed contract when it arrives.</span>
        </div>
      )}
      {asking && (
        <Dialog open onOpenChange={(o) => !o && setAsking(false)}>
          <DialogContent className="max-w-lg" data-testid="control-exception-dialog">
            <DialogHeader><DialogTitle>Approve an exception</DialogTitle>
              <DialogDescription>The project may start without a signed contract. It still needs its scope and an approved baseline budget. Your name, the date and this reason are recorded.</DialogDescription></DialogHeader>
            <div className="space-y-1.5">
              <Label htmlFor="exception-reason">Why can it start now?</Label>
              <Textarea id="exception-reason" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="control-exception-reason" placeholder="For example: the client's PO is promised for next week and site work must start now" />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAsking(false)}>Cancel</Button>
              <Button disabled={busy || reason.trim().length < 10} onClick={approve} data-testid="control-exception-submit">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Approve exception</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
